<template>
  <div class="custom-upload-estimate container-fluid">
    <!-- Blocks the page for the whole parse/submit. Every control is also bound to `busy`, so the
         form stays frozen even if this layer is dismissed. -->
    <z-overlay
      v-if="busy && !overlayHidden"
      :notice="parsing ? 'Parsing' : 'Submitting'"
      custom-style="position: fixed;"
      @cancel="overlayHidden = true"
    />
    <div class="row mb-2">
      <div class="col">
        <h5 class="mb-1">Upload Repair Estimate (PDF)</h5>
        <p class="text-muted mb-2">
          Upload a Mitchell1 or CCC estimate. We parse the repair lines for review — correct anything that
          looks off, then submit to ingest and generate the suggested calibrations.
        </p>
      </div>
    </div>

    <!-- Hidden input for click-to-browse -->
    <input ref="fileInput" type="file" accept="application/pdf" class="d-none" @change="onFileChange" />

    <!-- Drag & drop + controls (bottom-right; Source/OCR under "Advanced") -->
    <div v-if="!estimate && !parsing" class="row mb-3">
      <div class="col">
        <div
          class="custom-dropzone"
          :class="{ 'custom-dropzone-active': dragActive }"
          @dragover.prevent="dragActive = true"
          @dragenter.prevent="dragActive = true"
          @dragleave.prevent="dragActive = false"
          @drop.prevent="onDrop"
          @click="$refs.fileInput && $refs.fileInput.click()"
        >
          <i class="fas fa-file-pdf custom-dropzone-icon"></i>
          <div v-if="file"><strong>{{ file.name }}</strong> — ready to parse</div>
          <div v-else>Drag &amp; drop a Mitchell1 or CCC PDF here, or click to browse</div>
        </div>
        <div class="d-flex justify-content-end align-items-end gap-2 mt-2 custom-controls">
          <div class="custom-firm-picker">
            <label class="form-label custom-label mb-0">
              Target Firm<span v-if="!isSuperAdmin" class="text-muted"> (your firm)</span>
            </label>
            <!-- Super-admins can direct the upload to any firm; everyone else is locked to their own. -->
            <Multiselect
              v-if="isSuperAdmin"
              v-model="selectedFirm"
              class="form-control custom-multiselect"
              placeholder="search firm by name"
              :close-on-select="true"
              :filter-results="false"
              :min-chars="1"
              :resolve-on-load="false"
              :delay="400"
              :searchable="true"
              :options="async (query) => await fetchLocations(query)"
              label="firmName"
              value-prop="firm"
            />
            <input v-else class="form-control form-control-sm" :value="firmDisplay" disabled />
          </div>
          <div v-if="showAdvanced" class="d-flex align-items-end gap-2">
            <div>
              <label class="form-label custom-label mb-0">Source</label>
              <select class="form-select form-select-sm" v-model="source" :disabled="busy">
                <option value="">Auto-detect</option>
                <option value="CCC">CCC</option>
                <option value="Mitchell">Mitchell</option>
              </select>
            </div>
            <div>
              <label class="form-label custom-label mb-0">OCR Quality</label>
              <select class="form-select form-select-sm" v-model="quality" :disabled="busy">
                <option value="best">Best (slower)</option>
                <option value="fast">Fast</option>
              </select>
            </div>
          </div>
          <button class="btn btn-link btn-sm text-decoration-none" @click="showAdvanced = !showAdvanced">
            <i class="fas me-1" :class="showAdvanced ? 'fa-chevron-up' : 'fa-sliders-h'"></i>Advanced
          </button>
          <button class="btn btn-primary btn-sm" :disabled="!file || !targetFirmId || busy" @click="parse">
            <i class="fas fa-magic me-1"></i>{{ parsing ? 'Parsing…' : 'Parse' }}
          </button>
          <button v-if="file && !parsing" class="btn btn-outline-secondary btn-sm" :disabled="busy" @click="reset">Clear</button>
        </div>
      </div>
    </div>

    <div v-if="parsing" class="row mb-3">
      <div class="col text-muted">
        <span class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>Reading and parsing the document — scanned PDFs are slower (OCR)…
      </div>
    </div>

    <!-- Parse quality banner + the Cancel/Submit actions. The actions sit at the TOP of the review so
         they are reachable without scrolling past the whole line grid. -->
    <div v-if="estimate" class="row align-items-center mb-3">
      <div class="col">
        <div
          class="alert py-2 mb-0"
          :class="parseAlertClass"
          title="A heuristic for how cleanly the rows landed in the expected columns. It is not a measure of field accuracy, so check the lines."
        >
          <strong>Detected:</strong> {{ platformDisplay }} &nbsp;|&nbsp;
          <strong>Processing:</strong> {{ processingLabel }} &nbsp;|&nbsp;
          <strong>Parse quality:</strong> {{ qualityLabel }} &nbsp;|&nbsp;
          <strong>Lines:</strong> {{ lines.length }}
        </div>
      </div>
      <div class="col-auto text-end">
        <button class="btn btn-secondary btn-sm" :disabled="busy" @click="reset">Cancel</button>
        <button class="btn btn-success btn-sm ms-1" :disabled="busy || !canConfirm" @click="confirmSubmission">
          <i class="fas fa-check me-1"></i>{{ busy ? 'Please wait…' : 'Submit' }}
        </button>
      </div>
    </div>

    <!-- Side-by-side review -->
    <div v-if="estimate" class="row">
      <div class="custom-split">
      <!-- v-show, not v-if, so collapsing the pane does not tear down the embed and force the blob
           to reload when it comes back. -->
      <div class="custom-split-pane mb-3" v-show="pdfOpen">
        <div class="fw-bold mb-1">
          Original PDF
          <!-- Not every browser renders the embed below, so always offer a plain link out to the file. -->
          <a v-if="pdfUrl" :href="pdfUrl" target="_blank" class="custom-pane-link">
            <i class="fas fa-external-link-alt"></i> open in a new tab
          </a>
          <a href="#" class="custom-pane-link me-3" title="Collapse the PDF and widen the parsed estimate"
            @click.prevent="pdfOpen = false">
            <i class="fas fa-chevron-left"></i> hide
          </a>
        </div>
        <!-- v-show, not v-if, so the embed keeps its blob URL. The browser PDF plugin paints in a
             native layer above page content regardless of z-index, so it would cover the overlay;
             display:none is what actually removes that layer. -->
        <object v-if="pdfUrl" v-show="!busy" :data="pdfUrl" type="application/pdf" class="custom-pdf-frame">
          <p class="p-3">Unable to display the PDF inline. <a :href="pdfUrl" target="_blank">Open in a new tab</a>.</p>
        </object>
        <div v-if="pdfUrl && busy" class="custom-pdf-placeholder">
          <i class="fas fa-file-pdf me-2"></i>Hidden while the estimate is submitted.
        </div>
      </div>
      <div class="custom-split-pane mb-3">
        <!-- Above the title, not beside it: with the PDF collapsed this pane becomes the left-hand
             column, so the way back belongs at its top-left. -->
        <div v-if="!pdfOpen" class="custom-pane-restore">
          <a href="#" title="Show the PDF again" @click.prevent="pdfOpen = true">
            <i class="fas fa-chevron-right"></i> show PDF
          </a>
        </div>
        <div class="fw-bold mb-1">Parsed Estimate (editable)</div>
        <div class="custom-review-pane">
        <div class="row g-2">
          <div class="col-6">
            <label class="custom-label">VIN</label>
            <input class="form-control form-control-sm" :class="{ 'is-invalid': vinCheckFailed }" v-model="estimate.vin" maxlength="17" :disabled="busy" />
            <!-- A warning, not a block: some non-North American VINs legitimately fail the check. -->
            <small v-if="vinCheckFailed" class="text-danger">{{ vinCheckMessage }}</small>
          </div>
          <div class="col-6"><label class="custom-label">RO Number</label><input class="form-control form-control-sm" v-model="estimate.repairOrderNumber" :disabled="busy" /></div>
          <div class="col-3"><label class="custom-label">Year</label><input class="form-control form-control-sm" v-model="estimate.year" :disabled="busy" /></div>
          <div class="col-3"><label class="custom-label">Make</label><input class="form-control form-control-sm" v-model="estimate.make" :disabled="busy" /></div>
          <div class="col-6"><label class="custom-label">Model</label><input class="form-control form-control-sm" v-model="estimate.model" :disabled="busy" /></div>
          <div class="col-12">
            <label class="custom-label">
              Insurance
              <span class="text-muted ms-1"><input type="checkbox" v-model="insuranceOverride" :disabled="busy" @change="onInsuranceOverrideToggle" /> enter manually</span>
            </label>
            <Multiselect
              v-if="!insuranceOverride"
              v-model="selectedInsurance"
              class="form-control custom-multiselect"
              placeholder="select an insurer"
              :options="insuranceOptions"
              :searchable="true"
              :disabled="busy"
              label="name"
              value-prop="value"
              @change="onInsuranceSelect"
            />
            <input v-else class="form-control form-control-sm" v-model="estimate.insuranceCompany" placeholder="insurer name" :disabled="busy" />
          </div>
        </div>

        <!-- Editable lines (matches the ADAS Map estimate-lines layout) -->
        <div class="custom-lines-section">
        <button type="button" class="custom-section-header" @click="linesOpen = !linesOpen">
          <i class="fas" :class="linesOpen ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
          Line Items ({{ lines.length }})
          <span class="custom-section-hint">{{ linesOpen ? 'click to collapse' : 'click to expand' }}</span>
        </button>
        <div v-show="linesOpen" class="custom-lines-scroll">
        <table class="table table-bordered table-striped mb-0 custom-lines">
          <thead>
            <tr>
              <th class="col-num">#</th>
              <th class="col-supp">Supp</th>
              <th class="col-group">Group</th>
              <th class="col-desc">Description</th>
              <th class="col-oper">Oper</th>
              <th class="col-part">Part #</th>
              <th class="col-qty">Qty</th>
              <th class="col-price">Price</th>
              <th class="col-hrs">Labor Hrs</th>
              <th class="col-hrs">Paint Hrs</th>
              <th class="col-rm"></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(l, i) in lines" :key="i" :class="{ 'table-warning': (l.confidence || 1) < 0.4 }">
              <!-- The line number as PRINTED on the estimate, so the grid lines up with the PDF
                   beside it. Manually added lines fall back to the internal sequence. -->
              <td class="col-num text-muted">{{ l.sourceLineNum || (l.lineNum != null ? l.lineNum : i) }}</td>
              <td class="col-supp"><input class="form-control form-control-sm" v-model="l.supplementCode" placeholder="-" :disabled="busy" /></td>
              <td class="col-group"><input class="form-control form-control-sm" v-model="l.lineHeaderDesc" :title="l.lineHeaderDesc" :disabled="busy" /></td>
              <td class="col-desc"><input class="form-control form-control-sm" v-model="l.lineDesc" :title="l.lineDesc" :disabled="busy" /></td>
              <td class="col-oper"><input class="form-control form-control-sm" v-model="l.laborOperation" :disabled="busy" /></td>
              <td class="col-part"><input class="form-control form-control-sm" v-model="l.partNumber" :disabled="busy" /></td>
              <td class="col-qty"><input class="form-control form-control-sm text-end" type="number" step="any" v-model.number="l.quantity" :disabled="busy" /></td>
              <td class="col-price"><input class="form-control form-control-sm text-end" type="number" step="any" v-model.number="l.price" :disabled="busy" /></td>
              <!-- Blank stays blank: an empty cell means the estimate did not state hours, which is not
                   the same as zero. "Incl." shows as a placeholder when the source said so. -->
              <td class="col-hrs"><input class="form-control form-control-sm text-end" type="number" step="any" :placeholder="l.laborIncluded ? 'Incl.' : ''" v-model.number="l.laborHours" :disabled="busy" /></td>
              <td class="col-hrs"><input class="form-control form-control-sm text-end" type="number" step="any" v-model.number="l.paintHours" :disabled="busy" /></td>
              <td class="col-rm text-center"><button class="btn btn-link btn-sm text-danger p-0" :disabled="busy" @click="removeLine(i)"><i class="fas fa-times"></i></button></td>
            </tr>
            <tr v-if="!lines.length"><td colspan="11" class="text-center text-muted">No lines parsed.</td></tr>
          </tbody>
        </table>
        </div>
        </div>
        <div class="custom-review-foot">
          <button class="btn btn-outline-secondary btn-sm" :disabled="busy" @click="addLine"><i class="fas fa-plus me-1"></i>Add line</button>

          <!-- Notes -->
          <div v-if="warnings.length" class="mt-2">
            <button type="button" class="btn btn-link btn-sm p-0" @click="notesOpen = !notesOpen">
              <i class="fas" :class="notesOpen ? 'fa-chevron-down' : 'fa-chevron-right'"></i> Notes ({{ warnings.length }})
            </button>
            <ul v-show="notesOpen" class="custom-notes mb-0">
              <li v-for="(w, i) in warnings" :key="i">{{ w.message }}</li>
            </ul>
          </div>
        </div>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { authStore } from '@/stores/auth'
import { lookupStore } from '@/stores/lookup'
import { locationStore } from '@/stores/location'
import { estimateStore } from '@/stores/estimate'
import Multiselect from '@vueform/multiselect'
import ZOverlay from '@/components/utilities/elements/Overlay.vue'
import { vinCheckDigit } from '@/plugins/vin'
export default {
  name: 'UploadEstimateView',
  components: { Multiselect, ZOverlay },
  data() {
    return {
      file: null,
      source: '',
      quality: 'best',
      busy: false,
      parsing: false,
      // Set if the user dismisses the blocking layer on a slow request; the form stays disabled.
      overlayHidden: false,
      dragActive: false,
      showAdvanced: false,
      blobKey: null,
      fileName: null,
      contentType: null,
      byteSize: null,
      estimate: null,
      lines: [],
      pdfUrl: null,
      notesOpen: false,
      // Collapsing the PDF pane hands its width to the parsed estimate, which is the wide one.
      pdfOpen: true,
      // The line grid renders at full height, so it is collapsible for when the header fields and
      // notes are what you are working on.
      linesOpen: true,
      selectedFirm: null,
      insuranceOverride: false,
      selectedInsurance: null
    }
  },
  computed: {
    ...mapState(authStore, ['profile']),
    ...mapState(lookupStore, ['lookups']),
    // Issue 622: parse-quality banner (mirrors ADAS Map).
    parseConfidence() { return this.estimate ? (this.estimate.confidence || 0) : 0 },
    parseAlertClass() {
      const c = this.parseConfidence
      return c >= 0.75 ? 'alert-success' : (c >= 0.4 ? 'alert-warning' : 'alert-danger')
    },
    platformDisplay() {
      const p = this.estimate && this.estimate.platform
      if (!p) return '—'
      return p.toLowerCase() === 'ccc' ? 'CCC' : p
    },
    processingLabel() {
      return (this.estimate && this.estimate.processingMethod) === 'OCR' ? 'OCR (scanned document)' : 'Text-based'
    },
    qualityLabel() {
      const c = this.parseConfidence
      return c >= 0.75 ? 'High' : (c >= 0.4 ? 'Medium' : 'Review Carefully')
    },
    isSuperAdmin() {
      return !!(this.profile && this.profile.roles && this.profile.roles[0] === 1)
    },
    targetFirmId() {
      if (this.isSuperAdmin) return this.selectedFirm || (this.profile && this.profile.firm) || null
      return this.profile && this.profile.firm
    },
    firmDisplay() {
      const name = this.profile && this.profile.firmName
      const num = this.profile && this.profile.firm
      return name ? `${name} (${num})` : (num ? `Firm ${num}` : '—')
    },
    insuranceOptions() {
      return (this.lookups && this.lookups.Insurance) ? this.lookups.Insurance : []
    },
    warnings() {
      return (this.estimate && this.estimate.warnings) || []
    },
    canConfirm() {
      return !!(this.estimate && this.estimate.vin && this.lines.length > 0)
    },
    // 17 characters that fail the check digit (or use I, O, Q). Other lengths are left alone.
    vinCheckFailed() {
      const v = String((this.estimate && this.estimate.vin) || '').trim().toUpperCase()
      return v.length === 17 && vinCheckDigit(v) !== v[8]
    },
    vinCheckMessage() {
      return vinCheckDigit(this.estimate && this.estimate.vin) === null
        ? 'VIN contains invalid characters (I, O and Q are not used in VINs). Check it before submitting.'
        : 'VIN check digit (9th character) does not match, so the VIN may have a typo. Check it before submitting.'
    }
  },
  async mounted() {
    // Canonical insurance list for the dropdown (shared/global list, firm 1).
    await this.getLookups({ keys: ['Insurance'], firm: 1, refresh: false })
  },
  methods: {
    ...mapActions(lookupStore, ['getLookups']),
    ...mapActions(locationStore, ['searchLocations']),
    ...mapActions(estimateStore, ['parsePdf', 'confirmPdf']),
    async fetchLocations(query) {
      return await this.searchLocations({ searchterm: query, lookup: 5, firm: this.profile.firm })
    },
    onFileChange(e) {
      const files = e.target.files
      this.file = files && files.length ? files[0] : null
    },
    onDrop(e) {
      this.dragActive = false
      const files = e.dataTransfer && e.dataTransfer.files
      if (!files || !files.length) return
      const f = files[0]
      if (f.type === 'application/pdf' || (f.name || '').toLowerCase().endsWith('.pdf')) this.file = f
    },
    setPdfUrl(url) {
      if (this.pdfUrl) URL.revokeObjectURL(this.pdfUrl)
      this.pdfUrl = url
    },
    async parse() {
      if (!this.file || !this.targetFirmId) return
      this.busy = true
      this.parsing = true
      this.overlayHidden = false
      try {
        const result = await this.parsePdf({ file: this.file, firm: this.targetFirmId, source: this.source, quality: this.quality })
        this.blobKey = result.blobKey
        this.fileName = result.fileName
        this.contentType = result.contentType
        this.byteSize = result.byteSize
        this.estimate = result.estimate
        this.lines = (result.estimate && result.estimate.lines) || []
        this.matchInsurance()
        this.setPdfUrl(URL.createObjectURL(this.file))
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('parsePdf failed', e)
        alert('Failed to parse the PDF.')
      } finally {
        this.busy = false
        this.parsing = false
      }
    },
    addLine() {
      this.lines.push({ lineHeaderDesc: '', lineDesc: '', laborOperation: '', supplementCode: '', partNumber: '', quantity: null, price: null, laborHours: null, paintHours: null, manualEntry: true })
    },
    removeLine(i) {
      this.lines.splice(i, 1)
    },
    onInsuranceSelect(val) {
      const opt = this.insuranceOptions.find((o) => o.value === val)
      if (this.estimate) this.estimate.insuranceCompany = opt ? (opt.name || opt.value) : ''
    },
    onInsuranceOverrideToggle() {
      if (!this.insuranceOverride) this.matchInsurance()
    },
    matchInsurance() {
      this.selectedInsurance = null
      const raw = ((this.estimate && this.estimate.insuranceCompany) || '').trim()
      if (!raw) { this.insuranceOverride = false; return }
      const opts = this.insuranceOptions || []
      const lc = raw.toLowerCase()
      const nm = (o) => ((o && (o.name || o.value)) || '').toString().toLowerCase()
      const match = opts.find((o) => nm(o) === lc)
        || opts.find((o) => nm(o) && (nm(o).startsWith(lc) || lc.startsWith(nm(o))))
        || opts.find((o) => nm(o) && (nm(o).includes(lc) || lc.includes(nm(o))))
      if (match) {
        this.selectedInsurance = match.value
        this.estimate.insuranceCompany = match.name || match.value
        this.insuranceOverride = false
      } else {
        this.insuranceOverride = true
      }
    },
    async confirmSubmission() {
      if (!this.canConfirm) return
      // No confirm dialog on this page, so a failing VIN is confirmed here.
      if (this.vinCheckFailed && !window.confirm(`${this.vinCheckMessage}\n\nVIN: ${this.estimate.vin}\n\nSubmit with this VIN anyway?`)) return
      this.busy = true
      this.overlayHidden = false
      try {
        this.estimate.lines = this.lines
        const result = await this.confirmPdf({
          firm: this.targetFirmId,
          source: this.source,
          blobKey: this.blobKey,
          fileName: this.fileName,
          contentType: this.contentType,
          byteSize: this.byteSize,
          estimate: this.estimate
        })
        if (result && result.adasInspection) {
          this.$router.push('/inspections')
        } else {
          alert('Ingest completed but no inspection id was returned.')
        }
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error('confirmPdf failed', e)
        alert('Failed to ingest the estimate.')
      } finally {
        this.busy = false
      }
    },
    reset() {
      this.file = null
      this.estimate = null
      this.lines = []
      this.blobKey = null
      this.setPdfUrl(null)
      this.insuranceOverride = false
      this.selectedInsurance = null
      this.overlayHidden = false
      if (this.$refs.fileInput) this.$refs.fileInput.value = ''
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-label { font-size: 0.75rem; text-transform: uppercase; color: #6c757d; margin-bottom: 2px; }
.custom-pdf-frame { width: 100%; height: 70vh; border: 1px solid #dee2e6; }
// Issue 622: the line grid renders at its FULL height (the pane grows with the line count and the
// page scrolls) because collapsing it to the window height meant constant scrolling inside a short
// box. The section header collapses it instead. The PDF pane sticks so it stays beside the rows.
@media (min-width: 992px) {
  .custom-pdf-frame { position: sticky; top: 1rem; }
  .custom-review-foot { padding-top: 0.5rem; }
}
// Column headers stay in view against the page scroll.
.custom-lines thead th { position: sticky; top: 0; z-index: 1; background-color: #fff; }
.custom-lines-section {
  border: 1px solid #dee2e6;
  border-radius: 4px;
  margin-top: 0.75rem;
}
.custom-section-header {
  width: 100%;
  text-align: left;
  background: #f8f9fa;
  border: 0;
  border-bottom: 1px solid #dee2e6;
  padding: 0.4rem 0.6rem;
  font-weight: 600;
  font-size: 0.85rem;
  cursor: pointer;
}
.custom-section-hint { float: right; font-weight: 400; color: #8a949e; }
.custom-pane-link { float: right; font-size: 0.8rem; font-weight: 400; }
.custom-pane-restore { margin-bottom: 0.5rem; font-size: 0.8rem; font-weight: 400; }
// The two review panes. Stacks below lg, side by side above it.
.custom-split { display: flex; flex-wrap: wrap; width: 100%; }
.custom-split-pane { width: 100%; min-width: 0; }
@media (min-width: 992px) {
  // gap gives the PDF and the grid breathing room; flex:1 shares what is left evenly, so the gap is
  // subtracted automatically and a collapsed PDF pane hands its whole width over with no extra rule.
  .custom-split { flex-wrap: nowrap; gap: 1em; }
  .custom-split-pane { flex: 1 1 0; width: auto; }
}
// Stands in for the PDF embed while it is hidden (see the note on the object tag).
.custom-pdf-placeholder {
  display: flex; align-items: center; justify-content: center;
  height: 200px; border: 1px solid #dee2e6; border-radius: 4px;
  background-color: #f7f8fa; color: #8a949e; font-size: 0.85rem;
}
.custom-lines { font-size: 0.85rem; }
.custom-lines td, .custom-lines th { vertical-align: middle; }
.custom-lines .col-supp { width: 70px; }
.col-num { width: 44px; }
.custom-lines .col-oper { width: 90px; }
.custom-lines .col-part { width: 130px; }
.custom-lines .col-qty { width: 70px; }
.custom-lines .col-price { width: 90px; }
.custom-lines .col-hrs { width: 80px; }
.custom-lines .col-rm { width: 44px; }
// Group and Description get real room; a narrow pane scrolls sideways rather than squeezing them.
.custom-lines .col-group { min-width: 170px; }
.custom-lines .col-desc { min-width: 280px; }
.custom-lines-scroll { overflow-x: auto; }
.custom-notes { font-size: 0.8rem; color: #6c757d; padding-left: 1.1rem; }
.custom-dropzone {
  border: 2px dashed #b8c2cc; border-radius: 8px; padding: 2.5rem 1rem; text-align: center;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  color: #6c757d; cursor: pointer; transition: border-color 0.15s ease, background-color 0.15s ease;
}
.custom-dropzone:hover, .custom-dropzone-active { border-color: #0d6efd; background-color: rgba(13,110,253,0.05); color: #0d6efd; }
.custom-dropzone-icon { font-size: 2rem; margin-bottom: 0.5rem; }
// Issue 622: line up every control in the upload bar at one height (40px, to match
// the ADAS Map / CCP upload pages). A global .custom-multiselect rule forces the
// firm picker to 42px and form-select-sm is ~31px, so pin them all here. Scoped to
// the bar so the dense line-grid inputs below keep their own size.
.custom-controls {
  .form-select-sm,
  .form-control-sm {
    height: 40px;
    min-height: 40px;
  }
  // @vueform multiselect: drive height via min-height + its CSS var (a fixed
  // height clips the placeholder). height:auto overrides the global 42px rule
  // that leaks in from BlueprintHeader.
  .custom-multiselect {
    height: auto;
    min-height: 40px;
    --ms-min-height: 40px;
  }
  // Match the Parse / Clear buttons to the 40px control height.
  .btn {
    height: 40px;
    display: inline-flex;
    align-items: center;
  }
}
// Issue 622: keep the Target Firm picker a sensible width in the bottom-right control bar.
// Widened so a full firm name fits without being clipped.
.custom-firm-picker {
  width: 360px;
}
</style>
